import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  CreateHookBindingRequest,
  HookBinding,
  ProcessDefinition,
  UpdateHookBindingRequest,
  WorkflowApiService,
  WorkflowHookRegistryItem
} from './workflow-api.service';
import { forkJoin } from 'rxjs';

interface HookRegistryRow {
  moduleKey: string;
  entityName: string;
  actionKey: string;
  defaultFallback: string;
  binding?: HookBinding;
}

interface HookOption {
  label: string;
  value: string;
  moduleKey: string;
  entityName: string;
  actionKey: string;
  defaultFallback: string;
}

@Component({
  standalone: true,
  selector: 'ain-workflow-hook-bindings',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    CheckboxModule,
    FormsModule,
    RouterLink
  ],
  providers: [MessageService],
  template: `
    <ain-page [title]="t('workflow.hooks.bindings.title')" [description]="t('workflow.hooks.bindings.description')">
      <ain-info-hint
        [titleKey]="'workflow.hooks.bindings.help.title'"
        [descriptionKey]="'workflow.hooks.bindings.help.description'"
        [bulletKeys]="[
          'workflow.hooks.bindings.help.bullets.entitlement',
          'workflow.hooks.bindings.help.bullets.fallback',
          'workflow.hooks.bindings.help.bullets.agentBindings'
        ]" />

      <div class="toolbar">
        <a routerLink="/workflow/definitions">{{ t('workflow.definitions.title') }}</a>
        <a routerLink="/growth/agents/workflow-bindings">{{ t('workflow.hooks.bindings.agentBindingsLink') }}</a>
        <p-select
          [options]="moduleFilterOptions()"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="moduleFilter"
          (ngModelChange)="moduleFilterSignal.set($event)"
          [showClear]="true"
          [placeholder]="t('workflow.hooks.bindings.filterModule')"
          appendTo="body" />
        <button pButton type="button" icon="pi pi-plus" [label]="t('workflow.hooks.bindings.create')" (click)="openCreateDialog()"></button>
        <button pButton type="button" severity="secondary" icon="pi pi-refresh" [label]="t('common.refresh')" (click)="reload()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else {
        <p-table [value]="registryRows()" [paginator]="true" [rows]="15">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('workflow.hooks.bindings.columns.moduleKey') }}</th>
              <th>{{ t('workflow.hooks.bindings.columns.entityName') }}</th>
              <th>{{ t('workflow.hooks.bindings.columns.actionKey') }}</th>
              <th>{{ t('workflow.hooks.bindings.columns.defaultFallback') }}</th>
              <th>{{ t('workflow.hooks.bindings.columns.bound') }}</th>
              <th>{{ t('workflow.hooks.bindings.columns.processDefinition') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.moduleKey }}</td>
              <td>{{ row.entityName }}</td>
              <td>{{ row.actionKey }}</td>
              <td>{{ row.defaultFallback }}</td>
              <td>{{ row.binding?.isActive ? t('common.yes') : t('common.no') }}</td>
              <td>{{ processName(row.binding?.processDefinitionId) }}</td>
              <td>
                @if (row.binding) {
                  <button pButton type="button" severity="secondary" [text]="true" [label]="t('common.edit')" (click)="openEditDialog(row)"></button>
                } @else {
                  <button pButton type="button" [text]="true" [label]="t('workflow.hooks.bindings.bind')" (click)="openCreateDialog(row)"></button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>

    <p-dialog
      [header]="dialogMode() === 'edit' ? t('workflow.hooks.bindings.edit') : t('workflow.hooks.bindings.create')"
      [(visible)]="dialogVisible"
      [modal]="true"
      [style]="{ width: 'min(560px, 92vw)' }">
      <label>{{ t('workflow.hooks.bindings.fields.hook') }}</label>
      <p-select
        [options]="hookOptions()"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="selectedHookKey"
        (ngModelChange)="onHookSelected($event)"
        [disabled]="dialogMode() === 'edit'"
        [filter]="true"
        appendTo="body"
        [placeholder]="t('common.select')" />

      <label>{{ t('workflow.hooks.bindings.fields.processDefinition') }}</label>
      <p-select
        [options]="publishedDefinitionOptions()"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="form.processDefinitionId"
        [showClear]="true"
        [filter]="true"
        appendTo="body"
        [placeholder]="t('workflow.hooks.bindings.selectPublishedProcess')" />

      <label>{{ t('workflow.hooks.bindings.fields.fallbackBehavior') }}</label>
      <p-select
        [options]="fallbackOptions"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="form.fallbackBehavior"
        appendTo="body" />

      <div class="checkbox-row">
        <p-checkbox [(ngModel)]="form.isActive" [binary]="true" inputId="bindingActive" />
        <label for="bindingActive">{{ t('workflow.hooks.bindings.fields.active') }}</label>
      </div>

      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-bottom: 16px; }
    label { display: block; margin: 12px 0 6px; font-weight: 600; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; margin-top: 12px; }
    a { color: var(--ain-color-primary); font-weight: 600; }
  `]
})
export class WorkflowHookBindingsComponent implements OnInit {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly registry = signal<WorkflowHookRegistryItem[]>([]);
  readonly bindings = signal<HookBinding[]>([]);
  readonly definitions = signal<ProcessDefinition[]>([]);
  readonly moduleFilterSignal = signal<string | null>(null);
  readonly dialogMode = signal<'create' | 'edit'>('create');
  readonly editingBindingId = signal<string | null>(null);

  moduleFilter: string | null = null;
  dialogVisible = false;
  selectedHookKey = '';
  form: CreateHookBindingRequest = this.emptyForm();

  readonly fallbackOptions = [
    { label: 'DirectAllow', value: 'DirectAllow' },
    { label: 'DirectDeny', value: 'DirectDeny' },
    { label: 'Error', value: 'Error' }
  ];

  readonly moduleFilterOptions = computed(() => {
    const modules = [...new Set(this.registry().map(item => item.moduleKey))].sort();
    return modules.map(value => ({ label: value, value }));
  });

  readonly hookOptions = computed<HookOption[]>(() =>
    this.registry().map(item => ({
      label: `${item.moduleKey} / ${item.entityType} / ${item.action}`,
      value: `${item.moduleKey}|${item.entityType}|${item.action}`,
      moduleKey: item.moduleKey,
      entityName: item.entityType,
      actionKey: item.action,
      defaultFallback: item.defaultBehaviorWithoutWorkflow
    }))
  );

  readonly publishedDefinitionOptions = computed(() =>
    this.definitions()
      .filter(item => item.status === 'Published')
      .map(item => ({ label: `${item.name} (v${item.currentVersion})`, value: item.id }))
  );

  readonly registryRows = computed<HookRegistryRow[]>(() => {
    const bindingMap = new Map<string, HookBinding>();
    for (const binding of this.bindings()) {
      bindingMap.set(`${binding.moduleKey}|${binding.entityName}|${binding.actionKey}`, binding);
    }

    const filter = this.moduleFilterSignal();
    return this.registry()
      .filter(item => !filter || item.moduleKey === filter)
      .map(item => {
        const key = `${item.moduleKey}|${item.entityType}|${item.action}`;
        return {
          moduleKey: item.moduleKey,
          entityName: item.entityType,
          actionKey: item.action,
          defaultFallback: item.defaultBehaviorWithoutWorkflow,
          binding: bindingMap.get(key)
        };
      })
      .sort((a, b) =>
        `${a.moduleKey}${a.entityName}${a.actionKey}`.localeCompare(`${b.moduleKey}${b.entityName}${b.actionKey}`)
      );
  });

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    forkJoin({
      registry: this.api.listHookRegistry(),
      bindings: this.api.listHookBindings(),
      definitions: this.api.listDefinitions()
    }).subscribe({
      next: ({ registry, bindings, definitions }) => {
        this.registry.set(registry);
        this.bindings.set(bindings);
        this.definitions.set(definitions);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  processName(processDefinitionId?: string): string {
    if (!processDefinitionId) return '—';
    const match = this.definitions().find(item => item.id === processDefinitionId);
    return match ? `${match.name} (v${match.currentVersion})` : processDefinitionId;
  }

  openCreateDialog(row?: HookRegistryRow): void {
    this.dialogMode.set('create');
    this.editingBindingId.set(null);
    this.form = this.emptyForm();
    if (row) {
      this.selectedHookKey = `${row.moduleKey}|${row.entityName}|${row.actionKey}`;
      this.onHookSelected(this.selectedHookKey);
    } else {
      this.selectedHookKey = '';
    }
    this.dialogVisible = true;
  }

  openEditDialog(row: HookRegistryRow): void {
    if (!row.binding) return;
    this.dialogMode.set('edit');
    this.editingBindingId.set(row.binding.id);
    this.selectedHookKey = `${row.moduleKey}|${row.entityName}|${row.actionKey}`;
    this.form = {
      moduleKey: row.moduleKey,
      entityName: row.entityName,
      actionKey: row.actionKey,
      processDefinitionId: row.binding.processDefinitionId ?? null,
      processDefinitionVersionId: null,
      fallbackBehavior: row.binding.fallbackBehavior,
      isActive: row.binding.isActive,
      configuration: null
    };
    this.dialogVisible = true;
  }

  onHookSelected(key: string): void {
    const option = this.hookOptions().find(item => item.value === key);
    if (!option) return;
    this.form.moduleKey = option.moduleKey;
    this.form.entityName = option.entityName;
    this.form.actionKey = option.actionKey;
    if (this.dialogMode() === 'create') {
      this.form.fallbackBehavior = option.defaultFallback;
    }
  }

  save(): void {
    if (!this.form.moduleKey || !this.form.entityName || !this.form.actionKey) {
      this.messages.add({ severity: 'warn', summary: this.t('workflow.hooks.bindings.validation.hookRequired') });
      return;
    }

    if (this.dialogMode() === 'edit' && this.editingBindingId()) {
      const payload: UpdateHookBindingRequest = {
        processDefinitionId: this.form.processDefinitionId,
        processDefinitionVersionId: this.form.processDefinitionVersionId,
        fallbackBehavior: this.form.fallbackBehavior,
        isActive: this.form.isActive,
        configuration: this.form.configuration
      };
      this.api.updateHookBinding(this.editingBindingId()!, payload).subscribe({
        next: () => this.onSaved(),
        error: err => this.onSaveError(err)
      });
      return;
    }

    this.api.createHookBinding(this.form).subscribe({
      next: () => this.onSaved(),
      error: err => this.onSaveError(err)
    });
  }

  private onSaved(): void {
    this.dialogVisible = false;
    this.messages.add({ severity: 'success', summary: this.t('common.saved') });
    this.reload();
  }

  private onSaveError(err: { error?: { error?: string } }): void {
    const code = err?.error?.error;
    const summary = code ? this.t(`workflow.hooks.bindings.errors.${code}`, code) : this.t('common.saveFailed');
    this.messages.add({ severity: 'error', summary });
  }

  private emptyForm(): CreateHookBindingRequest {
    return {
      moduleKey: '',
      entityName: '',
      actionKey: '',
      processDefinitionId: null,
      processDefinitionVersionId: null,
      fallbackBehavior: 'DirectDeny',
      isActive: true,
      configuration: null
    };
  }

  t(key: string, fallback?: string): string {
    const value = this.localization.translate(key);
    return value === key && fallback ? fallback : value;
  }
}
